import { ref } from 'vue';
import type {NoneServiceModel, Service} from "@/types";

/**
 * Composable for managing all UI state in the Builder view
 * This includes modals, panels, dropdowns, and their associated data
 */
export function useBuilderState() {
  // Project and version management modals
  const showProjectPicker = ref(false);
  const selectedProject = ref<any>(null);
  
  const showCopyVersionModal = ref(false);
  const showPublishVersionModal = ref(false);
  const showTestingContextDropdown = ref(false);
  
  // Wizard designer
  const showWizardDesigner = ref(false);
  
  // Service-related modals and panels
  const showServiceDetails = ref(false);
  const selectedServiceId = ref<string | null>(null);
  // const selectedServiceData = ref<any>(null);
  
  const showAddServicePanel = ref(false);
  const addServiceParentId = ref<string | null>(null);
  
  const showDeleteServiceModal = ref(false);
  const serviceToDelete = ref<{ id: string; name: string } | null>(null);
  
  const showToggleServiceModal = ref(false);
  const serviceToToggle = ref<{
    id: string;
    name: string;
    isDisabled: boolean;
  } | null>(null);
  
  const showRunServiceModal = ref(false);
  const runServiceNodeId = ref<string | null>(null);
  const runServiceData = ref<any>(null);
  const restoreServiceDetailsOnModalClose = ref(false);
  const runModalWasCancelled = ref(false);
  
  const showRenameServiceModal = ref(false);
  const serviceToRename = ref<{ id: string; currentName: string } | null>(null);
  const renameInputValue = ref("");
  // "rename" for the existing right-click flow; "create" when the modal is repurposed to name a
  // just-added service. Drives the modal's title/button copy — the save path is identical.
  const renameModalMode = ref<"rename" | "create">("rename");
  
  const showDuplicateServiceModal = ref(false);
  const serviceToDuplicate = ref<{ id: string; name: string } | null>(null);

  const showEditParentsModal = ref(false);
  const serviceToEditParents = ref<{ id: string; name: string } | null>(null);

  // History and run-related state
  const selectedRun = ref<any>(null);
  const showRunServiceDetails = ref(false);
  const selectedRunServiceId = ref<string | null>(null);
  const selectedRunServiceData = ref<any>(null);
  const isHistoryPanelCollapsed = ref(false);
  
  // Version creation form state
  const createVersionOption = ref("keep-archived");
  const newVersionNumber = ref("v2.4");
  const publishMode = ref("preview");
  const releaseNotes = ref(
    "Fixed email template rendering issues and improved performance"
  );
  const versionType = ref("minor"); // 'patch', 'minor', 'major'
  
  // Functions to manage modals and panels
  function openProjectPicker() {
    showProjectPicker.value = true;
  }
  
  function closeProjectPicker() {
    showProjectPicker.value = false;
  }
  
  function openWizardDesigner() {
    showWizardDesigner.value = true;
  }
  
  function closeWizardDesigner() {
    showWizardDesigner.value = false;
  }
  
  function openServiceDetails(nodeId: string) {
    selectedServiceId.value = nodeId;
    showServiceDetails.value = true;
  }
  
  function closeServiceDetails() {
    showServiceDetails.value = false;
    selectedServiceId.value = null;
    //selectedServiceData.value = null;
  }
  
  function openAddServicePanel(parentId: string | null = null) {
    addServiceParentId.value = parentId;
    showAddServicePanel.value = true;
  }
  
  function closeAddServicePanel() {
    showAddServicePanel.value = false;
    addServiceParentId.value = null;
  }
  
  function openDeleteServiceModal(serviceId: string, serviceName: string) {
    serviceToDelete.value = { id: serviceId, name: serviceName };
    showDeleteServiceModal.value = true;
  }
  
  function closeDeleteServiceModal() {
    showDeleteServiceModal.value = false;
    serviceToDelete.value = null;
  }
  
  function openToggleServiceModal(serviceId: string, serviceName: string, isDisabled: boolean) {
    serviceToToggle.value = { id: serviceId, name: serviceName, isDisabled };
    showToggleServiceModal.value = true;
  }
  
  function closeToggleServiceModal() {
    showToggleServiceModal.value = false;
    serviceToToggle.value = null;
  }
  
  function openRunServiceModal(nodeId: string, nodeData: any) {
    runServiceNodeId.value = nodeId;
    runServiceData.value = nodeData;
    showRunServiceModal.value = true;
  }
  
  function closeRunServiceModal() {
    showRunServiceModal.value = false;
    runServiceNodeId.value = null;
    runServiceData.value = null;
    runModalWasCancelled.value = false;
  }
  
  function openRenameServiceModal(serviceId: string, currentName: string, mode: "rename" | "create" = "rename") {
    serviceToRename.value = { id: serviceId, currentName };
    renameInputValue.value = currentName;
    renameModalMode.value = mode;
    showRenameServiceModal.value = true;
  }

  function closeRenameServiceModal() {
    showRenameServiceModal.value = false;
    serviceToRename.value = null;
    renameInputValue.value = "";
    renameModalMode.value = "rename";
  }
  
  function openDuplicateServiceModal(serviceId: string, serviceName: string) {
    serviceToDuplicate.value = { id: serviceId, name: serviceName };
    showDuplicateServiceModal.value = true;
  }
  
  function closeDuplicateServiceModal() {
    showDuplicateServiceModal.value = false;
    serviceToDuplicate.value = null;
  }

  function openEditParentsModal(serviceId: string, serviceName: string) {
    serviceToEditParents.value = { id: serviceId, name: serviceName };
    showEditParentsModal.value = true;
  }

  function closeEditParentsModal() {
    showEditParentsModal.value = false;
    serviceToEditParents.value = null;
  }

  function openRunServiceDetails(nodeId: string, nodeData: any) {
    selectedRunServiceId.value = nodeId;
    selectedRunServiceData.value = nodeData;
    showRunServiceDetails.value = true;
  }
  
  function closeRunServiceDetails() {
    showRunServiceDetails.value = false;
    selectedRunServiceId.value = null;
    selectedRunServiceData.value = null;
  }
  
  function toggleHistoryPanel() {
    isHistoryPanelCollapsed.value = !isHistoryPanelCollapsed.value;
  }
  
  function toggleTestingContextDropdown() {
    showTestingContextDropdown.value = !showTestingContextDropdown.value;
  }
  
  // Version management modals
  function openCopyVersionModal() {
    showCopyVersionModal.value = true;
  }
  
  function closeCopyVersionModal() {
    showCopyVersionModal.value = false;
  }
  
  function openPublishVersionModal() {
    showPublishVersionModal.value = true;
  }
  
  function closePublishVersionModal() {
    showPublishVersionModal.value = false;
  }

  return {
    // Project and version state
    showProjectPicker,
    selectedProject,
    showCopyVersionModal,
    showPublishVersionModal,
    showTestingContextDropdown,
    
    // Wizard state
    showWizardDesigner,
    
    // Service state
    showServiceDetails,
    selectedServiceId,
    //selectedServiceData,
    showAddServicePanel,
    addServiceParentId,
    showDeleteServiceModal,
    serviceToDelete,
    showToggleServiceModal,
    serviceToToggle,
    showRunServiceModal,
    runServiceNodeId,
    runServiceData,
    restoreServiceDetailsOnModalClose,
    runModalWasCancelled,
    showRenameServiceModal,
    serviceToRename,
    renameInputValue,
    renameModalMode,
    showDuplicateServiceModal,
    serviceToDuplicate,
    showEditParentsModal,
    serviceToEditParents,

    // History and run state
    selectedRun,
    showRunServiceDetails,
    selectedRunServiceId,
    selectedRunServiceData,
    isHistoryPanelCollapsed,
    
    // Form state
    createVersionOption,
    newVersionNumber,
    publishMode,
    releaseNotes,
    versionType,
    
    // Functions
    openProjectPicker,
    closeProjectPicker,
    openWizardDesigner,
    closeWizardDesigner,
    openServiceDetails,
    closeServiceDetails,
    openAddServicePanel,
    closeAddServicePanel,
    openDeleteServiceModal,
    closeDeleteServiceModal,
    openToggleServiceModal,
    closeToggleServiceModal,
    openRunServiceModal,
    closeRunServiceModal,
    openRenameServiceModal,
    closeRenameServiceModal,
    openDuplicateServiceModal,
    closeDuplicateServiceModal,
    openEditParentsModal,
    closeEditParentsModal,
    openRunServiceDetails,
    closeRunServiceDetails,
    toggleHistoryPanel,
    toggleTestingContextDropdown,
    openCopyVersionModal,
    closeCopyVersionModal,
    openPublishVersionModal,
    closePublishVersionModal
  };
}